import React, { useState } from 'react';
import '../css/careerChart.css';
import emptyBarPattern from '../../../../../../asset-controller-mod/asset-serv/images/emptyBarPattern.png';
/* ── Types ───────────────────────────────────────────────── */

export interface CareerBarItem {
  label: string;
  value: number;
  color?: string;
}

export interface CareerChartProps {
  title?:        string;
  bars?:         CareerBarItem[];
  yMax?:         number;   // optional — auto-computed from data if omitted
  yStep?:        number;
  onMenuClick?:  () => void;
  onTitleClick?: () => void;
}

/* ── Defaults ────────────────────────────────────────────── */

const DEFAULT_BARS: CareerBarItem[] = [
  { label: 'Job Opening',     value: 0 },
  { label: 'Active Jobs',     value: 0 },
  { label: 'Close Jobs',      value: 0 },
  { label: 'Awaiting review', value: 0 },
  { label: 'Submission',      value: 0 },
];

/* ── Rank-based color palette ────────────────────────────── */
// Index 0 = highest value, index 4 = lowest value

const VALUE_RANK_COLORS = [
  '#2388FF', // rank 1 — highest
  '#4E9FFF', // rank 2
  '#8DC1FF', // rank 3
  '#C3DDFF', // rank 4
  '#DAEAFF', // rank 5 — lowest
];


function assignColorsByRank(bars: CareerBarItem[]): CareerBarItem[] {
  const sortedUnique = Array.from(new Set(bars.map((b) => b.value))).sort((a, b) => b - a);
  return bars.map((bar) => ({
    ...bar,
    color:
      VALUE_RANK_COLORS[sortedUnique.indexOf(bar.value)] ??
      /* istanbul ignore next */
      VALUE_RANK_COLORS[VALUE_RANK_COLORS.length - 1], //NOSONAR
  }));
}




const CareerChart: React.FC<CareerChartProps> = ({
  title       = 'Career',
  bars        = DEFAULT_BARS,
  yMax:       yMaxProp,
  yStep       = 10,
  onMenuClick,
  onTitleClick,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Assign colors by value rank (highest value = darkest blue)
  const coloredBars = assignColorsByRank(bars);

  // Compute yMax dynamically: start at 50, expand by 10 for each threshold exceeded
  const maxValue = Math.max(...bars.map((b) => b.value));
  const yMax = yMaxProp ?? (Math.ceil(Math.max(maxValue, 30) / 10) * 10);

  // Build y-axis labels from yMax down to 0
  const yLabels: number[] = [];
  for (let v = yMax; v >= 0; v -= yStep) yLabels.push(v);

  return (
    <div className="scl--cms-components-careerChart-card">

      {/* ── Header ── */}
      <div className="scl--cms-components-careerChart-header">
        <button
          className="scl--cms-components-careerChart-title"
          onClick={onTitleClick}
        >{title}</button>
      </div>

      {/* ── Chart body ── */}
      <div className="scl--cms-components-careerChart-body">

        {/* Y-axis */}
        <div className="scl--cms-components-careerChart-yAxis">
          {yLabels.map((v) => (
            <span key={v} className="scl--cms-components-careerChart-yLabel">{v}</span>
          ))}
        </div>

        {/* Bars + grid */}
        <div className="scl--cms-components-careerChart-barsArea">

          <div className="scl--cms-components-careerChart-gridLines">
            {yLabels.map((_, i) => ( 
              <div key={i} className="scl--cms-components-careerChart-gridLine" //NOSONAR
              />
            ))}
          </div>

          {/* Bars */}
          <div className="scl--cms-components-careerChart-bars">
            {coloredBars.map((bar, i) => {
              const fillPct   = (bar.value / yMax) * 100;
              const isHovered = hoveredIndex === i;

              return (
                <div //NOSONAR
                  key={bar.label}
                  className="scl--cms-components-careerChart-barCol"
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >

                <div className="scl--cms-components-careerChart-barWrap">
                  {isHovered && (
                    <div
                      className="scl--cms-components-careerChart-tooltip"
                      style={{ bottom: `calc(${fillPct}% + 5px)` }}
                    >
                      <span>{bar.label}: {bar.value}</span>
                      <span className="scl--cms-components-careerChart-tooltipBeak" />
                    </div>
                  )}

                  <div
                    className="scl--cms-components-careerChart-barEmpty"
                    style={{ backgroundImage: `url(${emptyBarPattern})` }}
                  />
                  <div
                    className="scl--cms-components-careerChart-barFill"
                    style={{
                      height:     `${fillPct}%`,
                      background: bar.color,
                    }}
                  />
                </div>
                  {/* X label */}
                  <span className="scl--cms-components-careerChart-barLabel">
                    {bar.label}
                  </span>
                </div>
              );
            })}
          </div>

        </div>
      </div>

    </div>
  );
};

export default CareerChart;